import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import PayerForm from '../components/PayerForm';
import PageSuggestion from '../components/PageSuggestion';
import { payerApi } from '../services/api';
import { Payer, CreatePayerRequest, UpdatePayerRequest } from '../types';

const Payers: React.FC = () => {
    const [payers, setPayers] = useState<Payer[]>([]);
    const [filteredPayers, setFilteredPayers] = useState<Payer[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPayer, setEditingPayer] = useState<Payer | null>(null);
    const [loading, setLoading] = useState(true);

    const itemsPerPage = 10;

    useEffect(() => {
        fetchPayers();
    }, []);

    useEffect(() => {
        const filtered = payers.filter(payer =>
            payer.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredPayers(filtered);
        setCurrentPage(1);
    }, [searchTerm, payers]);

    const fetchPayers = async () => {
        try {
            setLoading(true);
            const response = await payerApi.getAll();
            setPayers(response.data || []);
        } catch (error) {
            console.error('Erro ao buscar pagadores:', error);
            setPayers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (data: CreatePayerRequest) => {
        try {
            await payerApi.create(data);
            await fetchPayers();
            setIsModalOpen(false);
        } catch (error) {
            console.error('Erro ao criar pagador:', error);
        }
    };

    const handleUpdate = async (data: UpdatePayerRequest) => {
        if (!editingPayer) return;

        try {
            await payerApi.update(editingPayer.id, data);
            await fetchPayers();
            setIsModalOpen(false);
            setEditingPayer(null);
        } catch (error) {
            console.error('Erro ao atualizar pagador:', error);
        }
    };

    const handleEdit = (payer: Payer) => {
        setEditingPayer(payer);
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setEditingPayer(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingPayer(null);
    };

    const handleSubmit = async (data: CreatePayerRequest | UpdatePayerRequest) => {
        if (editingPayer) {
            await handleUpdate(data as UpdatePayerRequest);
        } else {
            await handleCreate(data as CreatePayerRequest);
        }
    };

    const columns = [
        { key: 'name', label: 'Nome' },
        { key: 'description', label: 'Descrição' },
        { key: 'category', label: 'Categoria' },
        {
            key: 'createdAt',
            label: 'Criado em',
            render: (value: string) => new Date(value).toLocaleDateString('pt-BR')
        }
    ];

    const totalPages = Math.ceil(filteredPayers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedData = filteredPayers.slice(startIndex, startIndex + itemsPerPage);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-secondary-600">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                <div>
                    <h1 className="text-2xl font-bold text-secondary-900">Pagadores</h1>
                    <p className="text-secondary-600">Gerencie seus pagadores e categorias</p>
                    <PageSuggestion>
                        Cadastre clientes, empregadores ou outras origens de receita para facilitar o controle dos recebimentos.
                    </PageSuggestion>
                </div>
                <button
                    onClick={handleAddNew}
                    className="btn-primary flex items-center justify-center"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Pagador
                </button>
            </div>

            <DataTable
                data={paginatedData}
                columns={columns}
                searchPlaceholder="Buscar por nome..."
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onEdit={handleEdit}
                editButtonText="Editar"
            />

            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={editingPayer ? 'Editar Pagador' : 'Adicionar Pagador'}
            >
                <PayerForm
                    payer={editingPayer}
                    onSubmit={handleSubmit}
                    onCancel={handleCloseModal}
                />
            </Modal>
        </div>
    );
};

export default Payers;



